import { useEffect } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGetPolicyInfo } from "utils/API/TrxNowAPI";
import { PolicyLookupSpinner } from "webapp/components/PolicyLookupSpinner";
import { TextField, FormControl } from "@mui/material";
import { devLog } from "utils/devLog";
import "./CARSMemberInformationForm.scss";

const COMPLETE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDaysSincePurchase = (purchaseDateStr) => {
  const purchaseDate = new Date(`${purchaseDateStr}T00:00:00`);
  const todayDate = new Date(`${getTodayDateString()}T00:00:00`);
  return Math.floor((todayDate - purchaseDate) / (1000 * 60 * 60 * 24));
};

const isPurchaseDateCovered = (purchaseDateStr, maxDays) => {
  if (!COMPLETE_DATE_PATTERN.test(purchaseDateStr)) {
    return false;
  }

  const today = getTodayDateString();
  if (purchaseDateStr > today) {
    return false;
  }

  const daysSincePurchase = getDaysSincePurchase(purchaseDateStr);
  return daysSincePurchase < maxDays;
};

const policyMap = new Map();
export default function CARSMemberInformationForm(props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchValue] = useState(props.currState.MemberLookup.memberRef);
  const [contractNumber] = useState(props.currState.MemberLookup.contractNumber);
  const [last8Vin] = useState(props.currState.MemberLookup.last8Vin);
  const [firstNameInput] = useState(props.currState.MemberLookup.firstName);
  const [lastNameInput] = useState(props.currState.MemberLookup.lastName);

  const [firstName, setFirstName] = useState(props.currState.FirstName);
  const [lastName, setLastName] = useState(props.currState.LastName);

  const [searchType] = useState(props.currState.MemberLookup.searchType);
  const [memberInfo, setMemberInfo] = useState(undefined);
  const [contacts, setContacts] = useState(undefined);
  const [selectedContact, setSelectedContact] = useState(null);

  const [displaySelectionComponent, setDisplaySelectionComponent] = useState(false);
  const [checkDuplicateName, setCheckDuplicateName] = useState(false);
  const [partnerCode] = useState(props.currState.localGroup);
  const [yesHasTrouble, setYesHasTrouble] = useState(false);
  const [numberOfDays, setNumberOfDays] = useState(0);

  useEffect(() => {
    onSubmit();
    checkDuplicateContact(firstName, lastName);
  }, []);

  useEffect(() => {
    checkDuplicateContact(firstName, lastName);
  }, [contacts]);

  const onLoad = (success) => {
    if (typeof props.onLoad === "function") {
      props.onLoad(success);
      setDisplaySelectionComponent(success);
    }
  };

  const onMemberInfo = (memberInfo, contactInfo) => {
    if (typeof props.onMemberInfo === "function") {
      props.onMemberInfo(memberInfo, contactInfo);
    }
  };

  const onSelect = (sc) => {
    setSelectedContact(sc);
    onMemberInfo(memberInfo, sc);
    if (sc === undefined) {
      setFirstName(props.context.state.FirstName);
      setLastName(props.context.state.LastName);
    } else {
      setFirstName(sc.firstName);
      setLastName(sc.lastName);
    }
  };

  const onSubmit = () => {
    setError("");

    if (policyMap.has(searchValue)) {
      onMemberInfo(policyMap.get(searchValue), selectedContact);
      setMemberInfo(policyMap.get(searchValue));
      setLoading(false);
      onLoad(true);
      return;
    }

    setLoading(true);
    apiGetPolicyInfo({
      data: {
        providerRef: props.currState.providerRef,
        last8Vin: last8Vin,
        contractNumber: contractNumber,
        firstName: firstNameInput,
        lastName: lastNameInput,
        searchType: searchType,
        partnerCode: partnerCode,
      },
      companyParams: props.currState.currentCompanyParams,
    })
      .then((json) => {
        const data = json?.data || {};

        if (typeof data.numberOfDays === "number") {
          setNumberOfDays(data.numberOfDays);
        }

        const foundMember = data.success && data.memberInfo && typeof data.memberInfo === "object";

        if (foundMember) {
          setMemberInfo(data.memberInfo);
          setContacts(Array.isArray(data.memberInfo.contacts) ? data.memberInfo.contacts : undefined);
          onLoad(true);
        } else {
          setContacts(undefined);
          setMemberInfo(undefined);
          setError(t("BWC.PolicyInformation.UnableToFindMember"));
          policyMap.delete(searchValue);
          onLoad(false);
        }

        setLoading(false);
      })
      .catch((e) => {
        devLog("error", e);
        setLoading(false);
        setContacts(undefined);
        setMemberInfo(undefined);
        setError(t("BWC.PolicyInformation.UnableToFindMember"));
        onLoad(false);
      });
  };

  const onUserBack = (e) => {
    e.preventDefault();
    if (typeof props.onUserBack === "function") {
      props.onUserBack();
    }
  };

  const checkDuplicateContact = (firstName, lastName) => {
    if (firstName !== "" && lastName !== "" && contacts !== undefined) {
      contacts.map((con) => {
        if (
          con.firstName.toUpperCase().trim() === firstName.toUpperCase().trim() &&
          con.lastName.toUpperCase().trim() === lastName.toUpperCase().trim()
        ) {
          setCheckDuplicateName(true);
        }
        return checkDuplicateContact;
      });
    }
  };

  const onClickYesHasTrouble = () => {
    const nextHasTrouble = !yesHasTrouble;
    setYesHasTrouble(nextHasTrouble);
    if (typeof props.onHavingTroubleChange === "function") {
      props.onHavingTroubleChange(nextHasTrouble);
    } else if (typeof props.setMNFMemberInfoData === "function") {
      props.setMNFMemberInfoData({
        ...props.MNFMemberInfoData,
        havingTrouble: nextHasTrouble,
      });
    }
  };

  const onChangePurchaseDate = (e) => {
    const purchaseDate = e.target.value;
    const covered = isPurchaseDateCovered(purchaseDate, numberOfDays);

    devLog("purchase date change:", { purchaseDate, numberOfDays, covered });

    if (typeof props.setMNFMemberInfoData === "function") {
      props.setMNFMemberInfoData({
        ...props.MNFMemberInfoData,
        purchaseDate,
        covered,
      });
    }
  };

  return (
    <>
      <div className="formContainer">
        {memberInfo && !error && displaySelectionComponent && !yesHasTrouble ? (
          <div className="policyData">
            <div className="policyHeader">
              <h4>{t("description.ContractInformationHeader4")}</h4>
              <hr />
              <div className="policy-info-line">
                <b>{t("ATF.PolicyInformation.ConfirmCustomerYear")}:</b> {memberInfo.year}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("ATF.PolicyInformation.ConfirmCustomerMake")}:</b> {memberInfo.make}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("ATF.PolicyInformation.ConfirmCustomerModel")}:</b> {memberInfo.model}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("ATF.PolicyInformation.ConfirmCustomerColor")}:</b> {memberInfo.color}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("description.DropdownOptions1")}:</b> {memberInfo.last8Vin}
              </div>
              <hr />
            </div>
          </div>
        ) : (
          <></>
        )}
        {contacts && !error & displaySelectionComponent && (
          <div className="policyData">
            <div className="policyHeader driverContainer">
              <h4 className="driver-header">{t("description.ContactInformationHeader")}</h4>
              <hr />
              <>
                {contacts.map((contact, index) => {
                  return (
                    <div
                      key={index}
                      value={contact}
                      className={`vehicle-list-item ${selectedContact === contact ? "selected" : ""}`}
                      onClick={() => {
                        onSelect(contact);
                      }}
                    >
                      <div className="contact-list driver-list">
                        <input
                          className="vehicleSelector"
                          type="radio"
                          name="policy-vehicle"
                          checked={selectedContact === contact ? "selected" : ""}
                          readOnly
                        />
                        <div value={contact} className="vehicle-list-item-col-1">
                          <h6 value={contact} className="name-h5">
                            {contact.firstName} {contact.lastName}
                          </h6>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            </div>
          </div>
        )}
        {memberInfo && !checkDuplicateName && !error && displaySelectionComponent ? (
          <>
            <div className="policyData">
              <div className="policyHeader driverContainer">
                <h4 className="driver-header">{t("description.AddContactInformationHeader")}</h4>
                <hr />
                <div className="contact-list driver-list">
                  <input
                    className="vehicleSelector"
                    type="radio"
                    name="policy-vehicle"
                    checked={selectedContact === undefined}
                    onClick={() => {
                      onSelect(undefined);
                    }}
                    readOnly
                  />
                  <div className="vehicle-list-item-col-1">
                    <h6 className="name-h5">
                      {props.currState.FirstName} {props.currState.LastName}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
      {error ? (
        <div className="errorSectionCYSU">
          <b className="error" style={{ color: "red" }}>
            {error}
          </b>
          <div className="member-search-failing-section">
            <h6>{t("description.TroubleFindingAccount")}</h6>
            <div className="member-search-failing-actions">
              <button
                type="button"
                className={`member-search-failing-btn ${
                  yesHasTrouble ? "member-search-failing-btn--primary" : "member-search-failing-btn--secondary"
                }`}
                onClick={onClickYesHasTrouble}
              >
                {t("description.Yes")}
              </button>
              <button
                type="button"
                className="member-search-failing-btn member-search-failing-btn--secondary"
                onClick={onUserBack}
              >
                {t("description.SearchAgain")}
              </button>
            </div>
          </div>
          {yesHasTrouble && (
            <div className="member-search-failing-section new-contact-input-container" style={{ paddingTop: "1rem" }}>
              <h6 style={{ marginBottom: "0" }}>{t("description.FillUpInformation")}</h6>
              <FormControl error={!!error} className="MNF-input-box">
                <TextField
                  id="outlined-basic-firstName"
                  label="First Name"
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData.firstName}
                  type="string"
                  onChange={(e) => {
                    props.setMNFMemberInfoData({
                      ...props.MNFMemberInfoData,
                      firstName: e.target.value.toUpperCase().trim(),
                    });
                  }}
                />
              </FormControl>

              <FormControl error={!!error} className="MNF-input-box">
                <TextField
                  id="outlined-basic-lastName"
                  label="Last Name"
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData.lastName}
                  type="string"
                  onChange={(e) => {
                    props.setMNFMemberInfoData({
                      ...props.MNFMemberInfoData,
                      lastName: e.target.value.toUpperCase().trim(),
                    });
                  }}
                />
              </FormControl>

              <FormControl error={!!error} className="MNF-input-box">
                <TextField
                  id="outlined-basic-phoneNumber"
                  label="Phone Number"
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData.phoneNumber}
                  type="string"
                  onChange={(e) => {
                    props.setMNFMemberInfoData({
                      ...props.MNFMemberInfoData,
                      phoneNumber: e.target.value.trim(),
                    });
                  }}
                />
              </FormControl>

              <FormControl error={!!error} className="MNF-input-box">
                <TextField
                  id="outlined-basic-contractNumber"
                  label="Contract Number"
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData.contractNumber}
                  type="string"
                  inputProps={{ inputMode: "string" }}
                  onChange={(e) => {
                    const nextValue = (e.target.value || "").toUpperCase().trim();
                    props.setMNFMemberInfoData({
                      ...props.MNFMemberInfoData,
                      contractNumber: nextValue.toUpperCase().trim(),
                    });
                  }}
                />
              </FormControl>

              <FormControl
                error={props.showVinError && props.MNFMemberInfoData.vin.length !== 17}
                className="MNF-input-box"
              >
                <TextField
                  id="outlined-basic-vin"
                  label={t("description.FullVin")}
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData.vin}
                  type="string"
                  error={props.showVinError && props.MNFMemberInfoData.vin.length !== 17}
                  helperText={
                    props.showVinError && props.MNFMemberInfoData.vin.length !== 17 ? "VIN must be 17 characters." : ""
                  }
                  inputProps={{ maxLength: 17 }}
                  onChange={(e) => {
                    const nextValue = (e.target.value || "").toUpperCase().trim().slice(0, 17);
                    props.setMNFMemberInfoData({
                      ...props.MNFMemberInfoData,
                      vin: nextValue,
                    });
                  }}
                />
              </FormControl>

              <FormControl error={!!error} className="MNF-input-box">
                <TextField
                  id="outlined-basic-purchaseDate"
                  label="Purchase Date"
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData.purchaseDate}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: getTodayDateString() }}
                  onChange={onChangePurchaseDate}
                />
              </FormControl>
            </div>
          )}
        </div>
      ) : (
        <></>
      )}
      {loading ? <PolicyLookupSpinner title={`ContractLookupTitleBWC`} message={`PolicyLookupMessage`} /> : <></>}
    </>
  );
}
